// Client-side image resize helper.
// Always transcodes to JPEG (PNG/WEBP/HEIC included) so photos stay light.
// Target longest side 2560px, JPEG q=0.85. Small JPEGs are kept as-is.

const IMG_EXT = /\.(jpe?g|png|webp|gif|bmp|hei[cf]|tiff?|avif)$/i;
const HEIC_RE = /(image\/hei[cf])|\.hei[cf]$/i;

export const isImageFile = (file) =>
  Boolean(file) && ((file.type && file.type.startsWith('image/')) || IMG_EXT.test(file.name || ''));

const processImage = (file, { maxSide = 2560, quality = 0.85 } = {}, originalName) => new Promise((resolve, reject) => {
  const keepThreshold = 600 * 1024; // small JPEGs pass through untouched
  const reader = new FileReader();
  reader.onerror = () => reject(reader.error);
  reader.onload = () => {
    const dataUrl = reader.result;
    if (file.type === 'image/jpeg' && file.size <= keepThreshold) {
      resolve({ dataUrl, filename: originalName, resized: false });
      return;
    }
    const img = new Image();
    img.onload = () => {
      const { width, height } = img;
      const longest = Math.max(width, height);
      const scale = longest > maxSide ? maxSide / longest : 1;
      const w = Math.round(width * scale);
      const h = Math.round(height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h); // white background for transparent PNGs
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, w, h);
      const out = canvas.toDataURL('image/jpeg', quality);
      resolve({ dataUrl: out, filename: originalName.replace(/\.(png|webp|hei[cf]|tiff?|bmp|gif|avif)$/i, '.jpg'), resized: true });
    };
    img.onerror = () => reject(new Error('Formato non leggibile dal browser'));
    img.src = dataUrl;
  };
  reader.readAsDataURL(file);
});

export const resizeImageFile = async (file, opts = {}) => {
  if (!isImageFile(file)) throw new Error('Non è un\'immagine');
  let input = file;
  if (HEIC_RE.test(file.type) || HEIC_RE.test(file.name || '')) {
    const heic2any = (await import('heic2any')).default;
    const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
    const blob = Array.isArray(converted) ? converted[0] : converted;
    input = new File([blob], file.name.replace(/\.hei[cf]$/i, '.jpg'), { type: 'image/jpeg' });
  }
  return processImage(input, opts, input.name || file.name);
};
